import type { GraphicComponentOption } from 'echarts';

import type { TimeSeriesChartSectionLabels } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { chartLayout } from '../config/chartLayout';
import { PLOT_SECTION_LABEL_ID, RANGE_TRACK_SECTION_LABEL_ID } from '../config/componentIds';
import { getTrackHeight } from './buildGridOption';

type BuildSectionLabelOptionParams = VisibleSectionLabels & {
  laneCount: number;
  hasNavigation: boolean;
  theme: TimeSeriesChartTheme;
};

type SectionLabelPosition = { left: number; top: number } | { left: number; bottom: number };

function buildLabel(
  id: string,
  text: string | undefined,
  position: SectionLabelPosition,
  { sectionLabel }: TimeSeriesChartTheme
): GraphicComponentOption {
  return {
    id,
    type: 'text',
    ...position,
    // Always present and only hidden when unused: graphic elements merge by id, so an element left
    // out of a later option would otherwise stay on the canvas with its old text.
    invisible: text === undefined,
    silent: true,
    z: 100,
    style: {
      // Overline typography is upper case; the canvas has no text-transform, so it happens here.
      text: text?.toLocaleUpperCase() ?? '',
      fill: sectionLabel.color,
      fontFamily: sectionLabel.fontFamily,
      fontSize: sectionLabel.fontSize,
      fontWeight: sectionLabel.fontWeight,
      lineHeight: sectionLabel.lineHeight,
    },
  };
}

/**
 * The plot and range-track titles, drawn by ECharts itself so they can never drift from the grids
 * they name: the track title sits in the header row buildGridOption reserves above the lanes, and
 * both are positioned from the same layout constants the grids are.
 */
export function buildSectionLabelOption({
  plotLabel,
  rangeTrackLabel,
  laneCount,
  hasNavigation,
  theme,
}: BuildSectionLabelOptionParams): GraphicComponentOption[] {
  const navigationHeight = hasNavigation ? chartLayout.navigationReservedHeight : 0;
  // Vertically centred in the header row, like the rest of the row's content.
  const headerPadding = (chartLayout.rangeTrackHeaderHeight - theme.sectionLabel.lineHeight) / 2;
  const { plot, rangeTrack } = chartLayout.sectionLabelPositions;

  return [
    buildLabel(PLOT_SECTION_LABEL_ID, plotLabel, plot, theme),
    buildLabel(
      RANGE_TRACK_SECTION_LABEL_ID,
      rangeTrackLabel,
      {
        left: rangeTrack.left,
        bottom: navigationHeight + getTrackHeight(laneCount) + headerPadding + rangeTrack.bottomOffset,
      },
      theme
    ),
  ];
}

type VisibleSectionLabels = {
  plotLabel: string | undefined;
  rangeTrackLabel: string | undefined;
};

/**
 * The labels that are actually drawn. An empty string counts as no label, and the track title only
 * exists while there are lanes: the grid reserves its header row on exactly the same condition.
 */
export function getVisibleSectionLabels(
  sectionLabels: TimeSeriesChartSectionLabels | undefined,
  laneCount: number
): VisibleSectionLabels {
  return {
    plotLabel: sectionLabels?.plot || undefined,
    rangeTrackLabel: (laneCount > 0 && sectionLabels?.rangeTrack) || undefined,
  };
}
